import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Award, Clock, Shield, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import ContactModal from '../components/ContactModal';
import ResponsiveCarousel from '../components/ResponsiveCarousel';
import AnimatedCounter from '../components/AnimatedCounter';
import { stats, services, pricingPackages, testimonials, faqs, companyStats, whyChooseUsPoints } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

// Services Carousel Component
const ServicesCarousel = ({ services, setIsModalOpen }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {services.map((service, index) => (
            <div key={service.id} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0">
              <Card className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#FF7F7F] overflow-hidden hover-lift h-full">
                <div className="relative h-48 overflow-hidden image-zoom">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#4B0082]/80 to-transparent group-hover:from-[#FF7F7F]/80 transition-all duration-300"></div>
                  <h3 className="absolute bottom-4 left-4 text-white font-bold text-xl transform group-hover:translate-y-[-4px] transition-transform">
                    {service.title}
                  </h3>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="flex items-center justify-between gap-3">
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center text-[#FF7F7F] font-semibold hover:text-[#FF6B6B] transition-colors group"
                    >
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4 icon-bounce" />
                    </Link>
                    <Button
                      size="sm"
                      className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white px-4 py-2 text-sm btn-shimmer hover-glow transition-all"
                      onClick={() => setIsModalOpen(true)}
                    >
                      Get Started
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4B0082] hover:bg-[#FF7F7F] hover:text-white transition-all z-10"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4B0082] hover:bg-[#FF7F7F] hover:text-white transition-all z-10"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-8">
        {services.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all ${
              index === selectedIndex % services.length
                ? 'bg-[#FF7F7F] w-8'
                : 'bg-gray-300'
            }`}
            onClick={() => emblaApi && emblaApi.scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};

// Testimonials Carousel Component
const TestimonialsCarousel = ({ testimonials }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0">
              <Card className="border-2 border-gray-100 hover:border-[#FF7F7F] transition-all hover:shadow-xl h-full">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h4 className="font-semibold text-[#4B0082]">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-[#FF7F7F] text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-gray-600 italic leading-relaxed">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4B0082] hover:bg-[#FF7F7F] hover:text-white transition-all z-10"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4B0082] hover:bg-[#FF7F7F] hover:text-white transition-all z-10"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all ${
              index === selectedIndex % testimonials.length
                ? 'bg-[#FF7F7F] w-8'
                : 'bg-gray-300'
            }`}
            onClick={() => emblaApi && emblaApi.scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <Helmet>
        <title>Trademark Registration Services $45 | Trademark Frontline</title>
        <meta name="description" content="Professional trademark registration starting at $45. Fast, reliable trademark filing with 98% approval rate. Protect your brand today!" />
      </Helmet>
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4B0082]/5 to-[#FF7F7F]/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="space-y-8 animate-fade-in">
              <div>
                <p className="text-[#FF7F7F] font-semibold text-lg mb-2">
                  Protect Your Business Identity with Expert Legal Support
                </p>
                <h1 className="text-5xl lg:text-6xl font-bold text-[#4B0082] leading-tight">
                  Trademark Registration Made Simple
                </h1>
              </div>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Secure your business name, logo, or slogan with Trademark Frontline — your reliable partner for fast and accurate trademark filings. Our experts guide you every step of the way with professional support.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white px-8 py-6 text-lg btn-shimmer hover-glow transform hover:scale-110 transition-all duration-300"
                  onClick={() => setIsModalOpen(true)}
                >
                  Register My Trademark
                  <ArrowRight className="ml-2 h-5 w-5 inline-block group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-[#4B0082] text-[#4B0082] hover:bg-[#4B0082] hover:text-white px-8 py-6 text-lg transition-all duration-300 hover-lift"
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Services
                </Button>
              </div>
            </div>

            {/* Hero Image - Without Floating Stats Card */}
            <div className="relative animate-fade-in-right">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl hover-lift image-zoom">
                <img
                  src="https://images.unsplash.com/photo-1702825328124-dab63d85490e"
                  alt="Legal Services"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4B0082]/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need This Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="image-zoom rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.pexels.com/photos/39584/censorship-limitations-freedom-of-expression-restricted-39584.jpeg"
                  alt="Brand Protection"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#FF7F7F] text-white rounded-xl p-6 shadow-xl max-w-xs hover-lift animate-bounce-in">
                <p className="font-semibold text-lg">Protect Your Brand Today</p>
                <p className="text-sm mt-2 opacity-90">Don't risk losing your business identity to competitors</p>
              </div>
            </div>
            
            <div>
              <p className="text-[#FF7F7F] font-semibold text-sm mb-2">WHAT WE DO</p>
              <h2 className="text-4xl font-bold text-[#4B0082] mb-6">
                Protect Your Brand's Identity Through Trademark Registration
              </h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                At Trademark Frontline, we specialize in protecting your brand through fast, affordable, and reliable trademark registration services across the USA. We simplify the legal process so you can focus on growing your business with peace of mind.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start group">
                  <CheckCircle2 className="h-6 w-6 text-[#FF7F7F] mr-3 mt-1 flex-shrink-0 icon-bounce" />
                  <div>
                    <h3 className="font-semibold text-[#4B0082] mb-1 group-hover:text-[#FF7F7F] transition-colors">Trademark Search & Clearance</h3>
                    <p className="text-gray-600">Comprehensive database searches to ensure your mark is available</p>
                  </div>
                </div>
                <div className="flex items-start group">
                  <CheckCircle2 className="h-6 w-6 text-[#FF7F7F] mr-3 mt-1 flex-shrink-0 icon-bounce" />
                  <div>
                    <h3 className="font-semibold text-[#4B0082] mb-1 group-hover:text-[#FF7F7F] transition-colors">USPTO Trademark Filing</h3>
                    <p className="text-gray-600">Expert preparation and filing of your trademark application</p>
                  </div>
                </div>
                <div className="flex items-start group">
                  <CheckCircle2 className="h-6 w-6 text-[#FF7F7F] mr-3 mt-1 flex-shrink-0 icon-bounce" />
                  <div>
                    <h3 className="font-semibold text-[#4B0082] mb-1 group-hover:text-[#FF7F7F] transition-colors">Office Action Responses</h3>
                    <p className="text-gray-600">Professional handling of any USPTO objections or questions</p>
                  </div>
                </div>
                <div className="flex items-start group">
                  <CheckCircle2 className="h-6 w-6 text-[#FF7F7F] mr-3 mt-1 flex-shrink-0 icon-bounce" />
                  <div>
                    <h3 className="font-semibold text-[#4B0082] mb-1 group-hover:text-[#FF7F7F] transition-colors">Trademark Renewals</h3>
                    <p className="text-gray-600">Timely reminders and assistance with trademark maintenance</p>
                  </div>
                </div>
              </div>

              <Button
                size="lg"
                className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white transition-all"
                onClick={() => setIsModalOpen(true)}
              >
                Register My Trademark
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">OUR SERVICES</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              Comprehensive Brand Protection Services
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              At Trademark Frontline, we offer a complete range of brand protection and business formation solutions tailored to meet your goals.
            </p>
          </div>

          <ServicesCarousel services={services} setIsModalOpen={setIsModalOpen} />
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">SIMPLE PROCESS STEPS</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              Three Simple Steps to Brand Protection
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Getting your trademark registered has never been easier. At Trademark Frontline, we've developed a seamless and efficient three-step process designed to eliminate confusion, reduce delays, and save you valuable time.
            </p>
          </div>

          <div className="relative">
            {/* Connection Lines - Desktop Only */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FF7F7F] via-[#FF7F7F] to-[#FF7F7F] mx-[20%]"></div>

            <ResponsiveCarousel
              items={[
                {
                  number: 1,
                  title: 'Submit Your Details',
                  description: 'Provide us with your brand name, logo, and basic business information through our secure online form.'
                },
                {
                  number: 2,
                  title: 'We Handle the Filing',
                  description: 'Our experts conduct a trademark search, prepare your application, and file it with the USPTO — accurately and efficiently.'
                },
                {
                  number: 3,
                  title: 'Get Official Protection',
                  description: 'Once approved, your trademark is officially registered, giving you exclusive legal rights and nationwide brand protection.'
                }
              ]}
              renderItem={(step, index) => (
                <div className="relative">
                  <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#FF7F7F]/20 hover:border-[#FF7F7F] transition-all hover:transform hover:scale-105">
                    <div className="w-16 h-16 bg-[#FF7F7F] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto relative z-10">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-bold text-[#4B0082] mb-4 text-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-center">
                      {step.description}
                    </p>
                  </div>
                </div>
              )}
              gridCols="md:grid-cols-3"
              gap="gap-8"
            />
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              className="bg-[#4B0082] hover:bg-[#3A0062] text-white px-8 transition-all"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Start Your Application
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">OUR PRICING</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              Pick Your Trademark Package
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Choose the package that best fits your needs. All packages include expert support and guidance throughout the process.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {pricingPackages['trademark-registration'].map((pkg, index) => (
              <Card
                key={index}
                className={`relative overflow-hidden transition-all duration-300 flex flex-col w-full md:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] ${
                  pkg.popular
                    ? 'border-4 border-[#FF7F7F] shadow-2xl transform scale-105'
                    : 'border-2 border-gray-200 hover:border-[#FF7F7F] hover:shadow-xl'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-[#FF7F7F] text-white px-4 py-1 text-sm font-semibold">
                    POPULAR
                  </div>
                )}
                <CardContent className="p-6 flex flex-col flex-grow">
                  <h3 className="text-2xl font-bold text-[#4B0082] mb-2">{pkg.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 h-12">{pkg.description}</p>
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold text-[#4B0082]">${pkg.price}</span>
                      {pkg.originalPrice && (
                        <span className="text-2xl text-gray-400 line-through ml-2">${pkg.originalPrice}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">+USPTO fees</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto mb-6 space-y-2 pr-2 flex-grow" style={{ scrollbarWidth: 'thin', scrollbarColor: '#FF7F7F #f1f1f1' }}>
                    {pkg.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-[#FF7F7F] mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    className={`w-full mt-auto ${
                      pkg.popular
                        ? 'bg-[#FF7F7F] hover:bg-[#FF6B6B]'
                        : 'bg-[#4B0082] hover:bg-[#3A0062]'
                    } text-white transition-all`}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">WHY CHOOSE US</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              Your Trusted Brand Protection Partner
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Whether you're just starting out or expanding your business, Trademark Frontline is your dedicated partner in brand protection. We offer clear communication, expert guidance, and a hassle-free experience every step of the way.
            </p>
          </div>

          <ResponsiveCarousel
            items={whyChooseUsPoints}
            renderItem={(point, index) => {
              const icons = [
                <Award className="h-8 w-8 text-[#FF7F7F] icon-pulse" />,
                <Shield className="h-8 w-8 text-[#FF7F7F] icon-spin" />,
                <Clock className="h-8 w-8 text-[#FF7F7F] icon-bounce" />
              ];
              
              return (
                <Card className="border-2 border-transparent hover:border-[#FF7F7F] transition-all hover:shadow-xl hover-lift animate-scale-in" style={{ animationDelay: `${index * 0.15}s` }}>
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#FF7F7F]/20 transition-colors">
                      {icons[index]}
                    </div>
                    <h3 className="text-xl font-bold text-[#4B0082] mb-4 group-hover:text-[#FF7F7F] transition-colors">{point.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{point.description}</p>
                  </CardContent>
                </Card>
              );
            }}
            gridCols="md:grid-cols-3"
            gap="gap-8"
          />
        </div>
      </section>

      {/* Company Stats Section */}
      <section className="py-20 bg-gradient-to-br from-[#4B0082] to-[#3A0062] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Track Record Speaks for Itself</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Trusted by thousands of businesses nationwide for professional trademark and business services.
            </p>
          </div>

          <ResponsiveCarousel
            items={companyStats}
            renderItem={(stat, index) => (
              <div className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all hover:transform hover:scale-105">
                  <div className="text-5xl font-bold text-[#FF7F7F] mb-3">
                    <AnimatedCounter target={stat.number} duration={2500} />
                  </div>
                  <div className="text-xl font-semibold mb-3">{stat.label}</div>
                  <p className="text-gray-300 text-sm">{stat.description}</p>
                </div>
              </div>
            )}
            gridCols="md:grid-cols-3"
            gap="gap-8"
          />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">TESTIMONIALS</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">What Our Clients Say</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Trusted by businesses nationwide for reliable trademark protection.
            </p>
          </div>

          <TestimonialsCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">FAQs</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 text-lg">
              Got questions? Find quick answers to common trademark concerns here.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.general.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white border-2 border-gray-100 rounded-lg px-6 hover:border-[#FF7F7F] transition-all"
              >
                <AccordionTrigger className="text-left font-semibold text-[#4B0082] hover:text-[#FF7F7F]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#FF7F7F] to-[#FF6B6B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Don't Risk Your Brand's Future!
              </h2>
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Unregistered trademarks can lead to costly legal battles. Protect your brand with Trademark Frontline's expert services—fast, reliable, and hassle-free. Secure your exclusive rights now before it's too late!
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">Special Offer: $45 Trademark Registration</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">24/7 Expert Support Available</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">98% Approval Success Rate</span>
                </div>
              </div>
            </div>

            <div>
              <ContactForm
                title="Start Protecting Your Brand Today"
                ctaText="Register Now - Only $45"
                showMessage={true}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
    </>
  );
};

export default Home;